import { theme as antdTheme } from 'antd';

/**
 * Tokens que NO dependen del tema: marca, semánticos, tipografía, radios,
 * alturas de control y breakpoints. Se aplican igual en claro y en oscuro; el
 * algoritmo de Ant Design ajusta el resto (superficies, textos, bordes).
 */
const tokensBase = {
  /* 🎯 Marca */
  colorPrimary: '#6E62C4',
  colorPrimaryHover: '#5B51B2',
  colorPrimaryActive: '#4B418F',

  /* 🎨 Semánticos (antd los adapta por tema) */
  colorInfo: '#6E62C4',
  colorSuccess: '#3BB273',
  colorWarning: '#F2B705',
  colorError: '#E05757',

  /* 📐 Bordes / radios */
  borderRadius: 12,

  /* 🔠 Tipografía */
  fontFamily:
    '"Darker Grotesque", -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, Helvetica, Arial, sans-serif',
  fontSize: 18,
  lineHeight: 1.45,

  /* 🧘 Espaciado */
  controlHeight: 44,

  /* 📱 Breakpoints personalizados */
  screenXS: 480,
  screenSM: 576,
  screenMD: 768,
  screenLG: 1025,
  screenXL: 1200,
  screenXXL: 1600,
};

/**
 * Solo se aplican en tema claro. En oscuro se omiten para que el darkAlgorithm
 * calcule superficies/textos/bordes coherentes en vez de arrastrar valores claros.
 */
const lightTokens = {
  colorTextBase: '#1E1E2D',
  colorTextSecondary: '#5A5D7A',
  colorTextDisabled: '#A0A3BD',
  colorBgBase: '#F7F8FC',
  colorBgLayout: '#F3F4FA',
  colorBgContainer: '#FFFFFF',
  colorBorder: '#E3E5F1',
};

/* Overrides ESTRUCTURALES de componentes (alturas, radios, pesos): igual en ambos temas. */
const componentsBase = {
  Button: {
    controlHeight: 44,
    borderRadius: 12,
    fontWeight: 600,
    primaryShadow: '0 6px 16px rgba(110, 98, 196, 0.25)',
  },
  Input: { controlHeight: 44, borderRadius: 10, fontWeight: 500 },
  Select: { controlHeight: 44, borderRadius: 10, fontWeight: 500 },
  DatePicker: { controlHeight: 44, borderRadius: 10 },
  Card: { borderRadiusLG: 16, paddingLG: 24 },
  Table: { fontSize: 14 },
  Menu: { itemHeight: 46, fontWeight: 600, borderRadius: 10 },
  Alert: { borderRadius: 12, fontWeight: 500 },
  Tag: { borderRadiusSM: 8, fontSizeSM: 12, fontWeight: 600 },
};

/* Overrides de COLOR de componentes, solo para claro (el chrome tenía estos fijos). */
const lightComponents = {
  Card: { boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)' },
  Table: {
    headerBg: '#F3F4FA',
    headerColor: '#1E1E2D',
    rowHoverBg: '#F7F8FC',
    borderColor: '#E3E5F1',
  },
  Layout: { bodyBg: '#F7F8FC', headerBg: '#FFFFFF', siderBg: '#FFFFFF' },
  Menu: { itemSelectedBg: '#ECEBFA', itemSelectedColor: '#6E62C4', itemHoverColor: '#6E62C4' },
};

/**
 * Construye el theme para el ConfigProvider según el tema resuelto.
 * @param {'light'|'dark'} resolved
 */
export function buildTheme(resolved) {
  const isDark = resolved === 'dark';
  const mergeComp = (base, light) => ({ ...base, ...(isDark ? {} : light) });

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      ...tokensBase,
      ...(isDark ? {} : lightTokens),
    },
    components: {
      ...componentsBase,
      Card: mergeComp(componentsBase.Card, lightComponents.Card),
      Table: mergeComp(componentsBase.Table, lightComponents.Table),
      Menu: mergeComp(componentsBase.Menu, lightComponents.Menu),
      Layout: isDark ? {} : lightComponents.Layout,
    },
  };
}

// Compatibilidad: export por defecto = tema claro.
const theme = buildTheme('light');
export default theme;
